import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { Button } from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';
import { smartDecode } from '../utils/redirect';

const Message = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
`;

export const Callback: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const code = search.get('code')?.trim() || '';
  const redirectParam = search.get('redirect')?.trim() || '';
  const redirectPath = useMemo(() => {
    if (!redirectParam) {
      return '/';
    }
    return smartDecode(redirectParam);
  }, [redirectParam]);

  useEffect(() => {
    let active = true;

    const run = async () => {
      if (!code) {
        setIsLoading(false);
        setError('回调地址缺少 code');
        return;
      }

      const response = await authService.completeOAuthLogin({
        code,
        redirectParam: redirectParam || undefined,
      });

      if (!active) {
        return;
      }

      if (response.success) {
        window.location.replace(redirectPath);
        return;
      }

      setIsLoading(false);
      setError(response.error || response.message || '登录失败');
    };

    run().catch((caught) => {
      if (!active) {
        return;
      }
      setIsLoading(false);
      setError(caught instanceof Error ? caught.message : '登录失败');
    });

    return () => {
      active = false;
    };
  }, [code, redirectParam, redirectPath]);

  return (
    <Layout title="统一登录回调" subtitle={error ? '登录失败' : '正在建立会话...'}>
      <Message>
        {error ? (
          <>
            <div style={{ color: theme.colors.error, display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
            <Button as={Link} to="/login" $fullWidth>
              返回登录
            </Button>
          </>
        ) : (
          <>
            <div style={{ color: theme.colors.textSecondary, display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
              <Loader2 size={18} className="spin" />
              <span>正在完成 OAuth2 登录</span>
            </div>
            {!isLoading ? (
              <Button as={Link} to="/login" $fullWidth>
                返回登录
              </Button>
            ) : null}
          </>
        )}
      </Message>
    </Layout>
  );
};
